'use strict';

import { environment } from '@quartz-iam-legacy/environments/environment';
import { MonitoringUtils } from '@quartz-iam-legacy/app/monitoring/monitoringUtils';

/**
 * HTTP Interceptor for Keycloak authentication.
 * Adds Authorization Bearer token to API requests, and reactively recovers
 * from 401s by forcing a token refresh and retrying the original request once.
 *
 * Only intercepts requests matching the configured API URL to prevent:
 * - CORS errors with Keycloak server
 * - Token leakage to unintended servers
 *
 * Uses lazy injection pattern to avoid cyclic dependencies.
 *
 * Shared across QuartzX legacy projects. Keep in sync with QuartzX.Web.GOV.Legacy.
 *
 * @module services.keycloakInterceptor
 */
angular.module('services.keycloakInterceptor', [])

.factory('keycloakInterceptor', ['$q', '$injector', function($q, $injector) {
  var keycloakProvider = null;
  var $http = null;
  var apiUrl = environment.apiUrl || '';

  // Marker so the responseError handler doesn't loop on a request it already retried.
  var RETRY_FLAG = '__keycloakInterceptorRetry';

  // Mirror of keycloakProvider._normalizeError — keycloak-js rejects with boolean `true`
  // or undefined in several paths; logging those raw is actively misleading.
  function _normalizeError(err) {
    if (err === undefined || err === null) {
      return '(no error details — keycloak-js discarded response body)';
    }
    if (typeof err === 'boolean') {
      return '(library rejected with boolean ' + String(err) + ')';
    }
    if (err instanceof Error) {
      return { name: err.name, message: err.message, stack: err.stack };
    }
    if (typeof err === 'object') {
      try {
        return { type: err.constructor ? err.constructor.name : 'object', details: err };
      } catch (e) {
        return String(err);
      }
    }
    return String(err);
  }

  return {
    /**
     * Request interceptor.
     * Called before each HTTP request is sent.
     * Adds Authorization header with fresh Bearer token for API requests.
     *
     * @param {object} config - HTTP request configuration
     * @returns {Promise<object>} Promise that resolves to modified config
     */
    request: function(config) {
      // Lazy inject keycloakProvider to avoid circular dependency
      if (!keycloakProvider) {
        keycloakProvider = $injector.get('keycloakProvider');
      }

      // Only add Authorization header to API requests
      if (!_shouldAddToken(config.url)) {
        return config;
      }

      // Get fresh access token and add to request
      return keycloakProvider.getAccessToken()
        .then(function(token) {
          // Clone config and add Authorization header
          config.headers = config.headers || {};
          config.headers['Authorization'] = 'Bearer ' + token;

          return config;
        })
        .catch(function(error) {
          var tag = '[keycloakInterceptor] Failed to get access token';
          var normalized = _normalizeError(error);
          var ctx = { url: config.url, online: navigator.onLine, visibility: document.visibilityState };
          console.error(tag, normalized, ctx);
          MonitoringUtils.captureMessage(tag, {
            level: 'warning',
            extra: { error: normalized, context: ctx },
          });

          // Return config without token (request will likely fail with 401)
          // This allows error handler to catch it and potentially redirect to login
          return config;
        });
    },

    /**
     * Response error interceptor — reactive 401 recovery.
     *
     * Backstops keycloak-js's `onAuthRefreshError` which does not fire for
     * network-error rejections of `updateToken()`. Without this, a refresh
     * that fails mid-call (offline at wake, transient DNS, etc.) sends the
     * original request unauthenticated and the user gets a toast with no
     * recovery path.
     *
     * Behaviour:
     *   1. Pass through non-401s and non-API URLs unchanged.
     *   2. On a fresh API 401, force a refresh, retry the request once.
     *   3. On retry-401 or refresh failure, redirect to login.
     *
     * @param {object} rejection - HTTP error response
     * @returns {Promise} Resolves with retried response, or rejects with original error
     */
    responseError: function(rejection) {
      if (!rejection || rejection.status !== 401) {
        return $q.reject(rejection);
      }

      var config = rejection.config;

      if (!config || !_shouldAddToken(config.url) || config[RETRY_FLAG]) {
        return $q.reject(rejection);
      }

      // Lazy inject keycloakProvider and $http to avoid circular dependency
      if (!keycloakProvider) {
        keycloakProvider = $injector.get('keycloakProvider');
      }
      if (!$http) {
        $http = $injector.get('$http');
      }

      console.warn('[keycloakInterceptor] 401 intercepted for', config.url);

      return keycloakProvider.getAccessToken()
        .then(function(token) {
          config[RETRY_FLAG] = true;
          config.headers = config.headers || {};
          config.headers['Authorization'] = 'Bearer ' + token;
          return $http(config);
        })
        .catch(function(retryErr) {
          // Either the refresh failed or the retried request also returned 401 —
          // either way, the session can't be silently recovered. Bounce to login.
          var tag = '[keycloakInterceptor] 401 recovery failed, redirecting to login';
          var normalized = _normalizeError(retryErr);
          var ctx = { url: config.url, online: navigator.onLine, visibility: document.visibilityState };
          console.error(tag, normalized, ctx);
          MonitoringUtils.captureMessage(tag, {
            level: 'error',
            extra: { error: normalized, context: ctx },
          });
          var authService = $injector.get('authService');
          var currentRoute = window.location.pathname + window.location.search;
          authService.login(currentRoute);
          return $q.reject(rejection);
        });
    },
  };

  /**
   * Determine if Authorization header should be added to this request.
   * Only add to requests targeting the configured API URL.
   *
   * @param {string} url - Request URL
   * @returns {boolean} True if token should be added, false otherwise
   */
  function _shouldAddToken(url) {
    // No API URL configured - don't add token
    if (!apiUrl) {
      return false;
    }

    // Check if URL starts with API URL
    return url.indexOf(apiUrl) === 0;
  }
}]);
